import moment, { Moment } from 'moment';
import { fill, size } from 'lodash';
import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

type CalendarProps = {
   disabledBefore?: Moment;
   disabledAfter?: Moment;
   range?: moment.MomentInput[];
   selectedMonth: Moment;
   selectedDate?: Moment;
   onChange?: (value?: Moment) => void;
   isOnAdminSite?: boolean
   hoveredDate?: Moment;
   onHoverDate?: (value?: Moment) => void;
};

const Calendar: React.FC<CalendarProps> = ({
   disabledBefore = moment(),
   disabledAfter = moment().add(10, 'year'),
   selectedMonth,
   selectedDate,
   range,
   onChange,
   isOnAdminSite = false,
   hoveredDate,
   onHoverDate,
}) => {
   const [firstDate, setFirstDate] = useState(selectedMonth.clone().startOf('month'));
   const [dayOfFirstDate, setDayOfFirstDate] = useState(selectedMonth.clone().startOf('month').weekday());
   const [daysInMonth, setDaysInMonth] = useState(selectedMonth.clone().daysInMonth());

   useEffect(() => {
      setFirstDate(selectedMonth.clone().startOf('month'));
      setDayOfFirstDate(selectedMonth.clone().startOf('month').weekday());
      setDaysInMonth(selectedMonth.clone().daysInMonth());
   }, [selectedMonth]);

   const dayOfWeeks = moment.weekdaysShort();

   return (
      <div className="grid grid-cols-7 flex-1 gap-y-1.5" style={isOnAdminSite ? { "--color-org-light-primary": "#E7F3F4", "--color-org-primary": "#62A4AB" } as React.CSSProperties : undefined}>
         {dayOfWeeks.map(day => (
            <div
               className="h-7 text-center text-[13px] font-semibold"
               key={day?.toLowerCase()}
            >
               {day}
            </div>
         ))}
         {fill(Array(42), null).map((_, index) => {
            const dayMoment = firstDate.clone().add(index - dayOfFirstDate, 'day');
            const activeFirst = dayMoment.isSame(moment(range?.[0]));
            const activeLast = dayMoment.isSame(moment(range?.[1]));
            const dayCenter =
               (size(range) === 2 && dayMoment.isAfter(moment(range?.[0])) && dayMoment.isBefore(moment(range?.[1]))) ||
               (size(range) === 1 && !!hoveredDate && dayMoment.isAfter(moment(range?.[0])) && dayMoment.isBefore(hoveredDate));
            const isSelected = selectedDate && dayMoment.clone().local().isSame(selectedDate.clone().local(), 'day');

            const cssActive = activeFirst || activeLast ? ` bg-orgPrimary font-bold` : '';
            const cssFirst = activeFirst ? ' rounded-l-md' : '';
            const cssLast = activeLast ? ' rounded-r-md' : '';
            const cssCenter = dayCenter ? ` bg-orgLightPrimary` : '';
            const cssSelected = isSelected ? ' bg-orgPrimary hover:enabled:bg-orgPrimary text-white font-bold' : '';

            const disabled = dayMoment.isBefore(disabledBefore.startOf('day')) || dayMoment.isAfter(disabledAfter.startOf('day'))

            return index >= dayOfFirstDate && index < daysInMonth + dayOfFirstDate
               ? (
                  <button
                     className={twMerge(`py-3.5 px-2 flex items-center justify-center font-semibold text-sm`, cssActive, cssFirst, cssLast, cssCenter, cssSelected, activeFirst || activeLast ? 'hover:enabled:[background:linear-gradient(0deg,rgba(0,0,0,0.30)_0%,rgba(0,0,0,0.30)_100%),var(--color-org-primary,#001545)]' : 'hover:enabled:bg-orgLightPrimary')}
                     key={index}
                     onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        onChange?.(dayMoment);
                     }}
                     onMouseEnter={() => onHoverDate?.(dayMoment)}
                     onMouseLeave={() => onHoverDate?.(undefined)}
                     disabled={disabled}
                  >
                     <span
                        className={`text-sm
                  ${disabled && 'text-[#d7d7d7]'}
                  ${dayMoment.isSame(moment().startOf('day')) && (!selectedDate || !isSelected) && `text-orgPrimary`}
                  ${activeFirst || activeLast ? 'text-white' : ''}`}
                     >
                        {dayMoment.date()}
                     </span>
                  </button>
               )
               : <div key={index} />
         })}
      </div>
   );
};

export default Calendar;